import { useRef } from 'react';
import type { Archive } from '../../api/archives';
import {
  annotationItemId,
  stripHtml,
  type PandoraAnnotation,
  type PandoraItem,
} from '../../api/pandora';
import { posterUrl } from '../../engine/mediaurls';
import { formatTime } from '../../format/helpers';

interface Props {
  archive: Archive;
  mode: 'items' | 'moments';
  items: PandoraItem[];
  moments: PandoraAnnotation[];
  loading: boolean;
  exhausted: boolean;
  error: string | null;
  /** id of the item/annotation just added to the bin (transient ✓ feedback) */
  binnedId: string | null;
  onLoadMore(): void;
  onOpen(itemId: string, preset?: { concatIn: number; concatOut: number }): void;
  onBinItem(item: PandoraItem): void;
  onBinMoment(m: PandoraAnnotation): void;
}

/** Poster-card results grid with SearchPanel-style infinite scroll. */
export default function ResultGrid({
  archive,
  mode,
  items,
  moments,
  loading,
  exhausted,
  error,
  binnedId,
  onLoadMore,
  onOpen,
  onBinItem,
  onBinMoment,
}: Props) {
  const listRef = useRef<HTMLDivElement | null>(null);

  function onScroll() {
    const el = listRef.current;
    if (!el || loading || exhausted) return;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - 240) onLoadMore();
  }

  return (
    <div className="adv-results" ref={listRef} onScroll={onScroll}>
      {error && <div className="search-error">{error}</div>}

      <div className="adv-grid">
        {mode === 'items' &&
          items.map((item) => (
            <div
              key={item.id}
              className="adv-card"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(item.id)}
              onKeyDown={(e) => e.key === 'Enter' && onOpen(item.id)}
            >
              <img
                className="adv-card-poster"
                src={posterUrl(archive, item.id)}
                alt=""
                loading="lazy"
              />
              <span className="adv-card-title">{item.title || item.id}</span>
              <span className="adv-card-meta">
                {[item.director?.join(', '), item.year].filter(Boolean).join(' · ')}
              </span>
              <span className="adv-card-meta timecode">
                {item.duration ? formatTime(item.duration) : '—'}
                {item.parts > 1 ? ` · ${item.parts} parts` : ''}
              </span>
              <button
                className="ghost adv-card-bin"
                title="Save to bin"
                onClick={(e) => {
                  e.stopPropagation();
                  onBinItem(item);
                }}
              >
                {binnedId === item.id ? '✓' : '+ Bin'}
              </button>
            </div>
          ))}

        {mode === 'moments' &&
          moments.map((m) => {
            const itemId = annotationItemId(m.id);
            return (
              <div
                key={m.id}
                className="adv-card"
                role="button"
                tabIndex={0}
                onClick={() => onOpen(itemId, { concatIn: m.in, concatOut: m.out })}
                onKeyDown={(e) =>
                  e.key === 'Enter' && onOpen(itemId, { concatIn: m.in, concatOut: m.out })
                }
              >
                <img
                  className="adv-card-poster"
                  src={posterUrl(archive, itemId)}
                  alt=""
                  loading="lazy"
                />
                <span className="adv-card-title">{m.title || itemId}</span>
                <span className="adv-card-meta timecode">
                  {formatTime(m.in)} – {formatTime(m.out)}
                </span>
                <span className="adv-card-snippet">{stripHtml(m.value)}</span>
                <button
                  className="ghost adv-card-bin"
                  title="Save this moment to the bin"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBinMoment(m);
                  }}
                >
                  {binnedId === m.id ? '✓' : '+ Bin'}
                </button>
              </div>
            );
          })}
      </div>

      {loading && <div className="search-hint">loading…</div>}
      {!loading &&
        exhausted &&
        (mode === 'items' ? items.length === 0 : moments.length === 0) &&
        !error && <div className="search-hint">no results</div>}
    </div>
  );
}
